import { useMemo, useState } from 'react';
import {
  Button,
  HelpTip,
  Input,
  RISK_SEVERITIES,
  RISK_SEVERITY_LABELS,
  RISK_STATUSES,
  RISK_STATUS_LABELS,
  ROOM_ITEM_VISIBILITIES,
  VISIBILITY_LABELS,
  type CreateRiskRequest,
  type RiskOwnerOption,
  type RiskSeverity,
  type RiskStatus,
  type RoomItemVisibility,
  type RoomRisk,
  type UpdateRiskRequest,
  useSingleSubmit,
} from '@dataroom/shared';
import {
  EMPTY_RISK_FORM,
  OTHER_OWNER,
  createRiskBody,
  riskFormFrom,
  riskFormProblem,
  updateRiskBody,
  type RiskFormState,
} from '@/lib/risk-form';
import { Modal } from './Modal';

const fieldClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

const OWNER_ROLE_LABELS: Record<RiskOwnerOption['role'], string> = {
  admin: 'Room admin',
  deal_team: 'Deal team',
  seller: 'Seller',
};

type RiskDialogProps = {
  ownerOptions: RiskOwnerOption[];
  pending: boolean;
  error: string | null;
  onClose: () => void;
} & (
  | { risk?: undefined; onCreate: (body: CreateRiskRequest) => void }
  | { risk: RoomRisk; onUpdate: (body: UpdateRiskRequest) => void }
);

/**
 * Add or edit one risks & issues entry (ADR-0047). The owner is ONE choice: a
 * deal-side person from the room (never a buyer) or "Someone else" with a typed
 * name — the form cannot hold both. An edit sends only what changed. Entries are
 * written by people; the AI never adds or judges them. Presentational: the page
 * owns the mutation and its pending/error state.
 */
export function RiskDialog(props: RiskDialogProps) {
  const { ownerOptions, pending, error, onClose } = props;
  const initial = useMemo(
    () => (props.risk ? riskFormFrom(props.risk) : EMPTY_RISK_FORM),
    [props.risk],
  );
  const [form, setForm] = useState<RiskFormState>(initial);
  const set = <K extends keyof RiskFormState>(key: K, value: RiskFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const editing = props.risk !== undefined;
  const problem = riskFormProblem(form);
  const patch = editing ? updateRiskBody(initial, form) : null;
  const unchanged = patch !== null && Object.keys(patch).length === 0;
  const canSubmit = !problem && !unchanged && !pending;
  // An owner who has since left the room stays selectable (and untouched) on edit.
  const departedOwner =
    props.risk?.ownerActorId && !ownerOptions.some((o) => o.actorId === props.risk?.ownerActorId)
      ? { actorId: props.risk.ownerActorId, name: props.risk.owner ?? 'A former participant' }
      : null;

  // One entry per click, even on a quick double-click (full-deal test 2026-10-08).
  const beginSubmit = useSingleSubmit();
  function save() {
    if (!canSubmit || !beginSubmit(JSON.stringify(form))) return;
    if (props.risk !== undefined) props.onUpdate(updateRiskBody(initial, form));
    else props.onCreate(createRiskBody(form));
  }

  return (
    <Modal
      title={editing ? 'Edit risk or issue' : 'Add a risk or issue'}
      description="Record something that could affect the deal, who owns it and when it should be dealt with. Entries are written by the deal team — the AI never adds or judges them."
      onClose={onClose}
      footer={
        <>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : (
            <span className="text-xs text-muted-foreground">
              {form.title.trim() && problem ? problem : 'Open entries appear in the weekly digest.'}
            </span>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button size="sm" disabled={!canSubmit} onClick={save}>
              {pending ? 'Saving…' : editing ? 'Save changes' : 'Add entry'}
            </Button>
          </div>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="risk-title" className="text-sm font-medium">
            Title
          </label>
          <Input
            id="risk-title"
            placeholder="e.g. Key customer contract has a change-of-control clause"
            value={form.title}
            maxLength={200}
            onChange={(e) => set('title', e.target.value)}
            disabled={pending}
            autoFocus
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="risk-description" className="text-sm font-medium">
            Description <span className="text-muted-foreground">(optional)</span>
          </label>
          <textarea
            id="risk-description"
            rows={3}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            value={form.description}
            maxLength={4000}
            onChange={(e) => set('description', e.target.value)}
            disabled={pending}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="risk-severity" className="text-sm font-medium">
              Severity
            </label>
            <select
              id="risk-severity"
              className={fieldClass}
              value={form.severity}
              onChange={(e) => set('severity', e.target.value as RiskSeverity)}
              disabled={pending}
            >
              {RISK_SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {RISK_SEVERITY_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="risk-status" className="text-sm font-medium">
              Status
            </label>
            <select
              id="risk-status"
              className={fieldClass}
              value={form.status}
              onChange={(e) => set('status', e.target.value as RiskStatus)}
              disabled={pending}
            >
              {RISK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {RISK_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="risk-owner" className="text-sm font-medium">
              Owner <span className="text-muted-foreground">(optional)</span>
            </label>
            <select
              id="risk-owner"
              className={fieldClass}
              value={form.ownerChoice}
              onChange={(e) => {
                const choice = e.target.value;
                // Leaving "Someone else" drops the typed name: one owner, never two.
                setForm((f) => ({
                  ...f,
                  ownerChoice: choice,
                  ownerText: choice === OTHER_OWNER ? f.ownerText : '',
                }));
              }}
              disabled={pending}
            >
              <option value="">No owner yet</option>
              {ownerOptions.map((o) => (
                <option key={o.actorId} value={o.actorId}>
                  {o.name} — {OWNER_ROLE_LABELS[o.role]}
                </option>
              ))}
              {departedOwner ? (
                <option value={departedOwner.actorId}>
                  {departedOwner.name} — no longer in the room
                </option>
              ) : null}
              <option value={OTHER_OWNER}>Someone else…</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="risk-due" className="text-sm font-medium">
              Due date <span className="text-muted-foreground">(optional)</span>
            </label>
            <input
              id="risk-due"
              type="date"
              className={fieldClass}
              value={form.dueDate}
              onChange={(e) => set('dueDate', e.target.value)}
              disabled={pending}
            />
          </div>
        </div>

        {form.ownerChoice === OTHER_OWNER ? (
          <div className="space-y-1.5">
            <label htmlFor="risk-owner-text" className="text-sm font-medium">
              Owner&apos;s name
            </label>
            <Input
              id="risk-owner-text"
              placeholder="e.g. Seller's outside counsel"
              value={form.ownerText}
              maxLength={200}
              onChange={(e) => set('ownerText', e.target.value)}
              disabled={pending}
              aria-describedby="risk-owner-text-hint"
            />
            <p id="risk-owner-text-hint" className="text-xs text-muted-foreground">
              Shown exactly as typed to everyone who can see this entry.
            </p>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <div className="flex items-center gap-1">
            <label htmlFor="risk-visibility" className="text-sm font-medium">
              Who can see it
            </label>
            <HelpTip id="admin.risks.visibility" className="ml-1" />
          </div>
          <select
            id="risk-visibility"
            className={fieldClass}
            value={form.visibility}
            onChange={(e) => set('visibility', e.target.value as RoomItemVisibility)}
            disabled={pending}
            aria-describedby="risk-visibility-hint"
          >
            {ROOM_ITEM_VISIBILITIES.map((v) => (
              <option key={v} value={v}>
                {VISIBILITY_LABELS[v]}
              </option>
            ))}
          </select>
          <p id="risk-visibility-hint" className="text-xs text-muted-foreground">
            Applies in the app and in the weekly digest. Buyers see an owner as a role, never a
            name.
          </p>
        </div>
      </div>
    </Modal>
  );
}
